import {
  VTextField,
  VTextarea,
  VSelect,
  VAutocomplete,
  VSwitch,
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import ProductSelector from '@/components/form/ProductSelector.vue'
import FileUploader from '@/components/form/FileUploader.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';

import type { FormField } from '@/types/form';

import QuotationDetail from '@/components/modules/quotations/Detail.vue'

function getHostnameSafe(): string {
  // Navegador
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  // SSR / Node (si lo necesitas, puedes leer una var de entorno)
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
}

// Usa comparación estricta; ajusta si necesitas subdominios.
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

const idFieldMappings: Record<string, string> = {
  user_id: 'user',
  opportunity_id: 'opportunity'
};

export const CREATE_FORM: FormField[] = [  
  { value: 'user_id', 
    permission_state:'hidden',
    permission:'activities.assign',
    label: 'Vendedor', 
    component: VAutocomplete, 
    required: false, 
    col: 6, 
    fetchOptions: 'searchUsers', 
    searchValue: 'name', 
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '', 
    itemValue: 'id',
    fetchParams: () => ({
      'filter[is_active]': 1
    }),
  },
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete,
    searchValue: 'name', 
    required: true, 
    col: 6, 
    mdcol:12, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject_type: value?.type,
    })
  },
  ...(IS_DOMINIOMEDIOS ? [{ value: 'campaign', label: 'Campaña', component: VTextField, required: false, col: 6, mdcol:12 }] : []),
  ...(IS_DOMINIOMEDIOS ? [{
    value: 'period',
    label: 'Periodo',
    component: VTextField,
    required: false,
    col: 6,
    mdcol: 12
  }] : []),
  {
    value: 'opportunity',
    label: 'Oportunidad',
    component: VAutocomplete,
    required: false,
    col: 6,
    fetchOptions: 'searchOpportunity',
    searchValue: 'opportunity_name',
    itemText: 'opportunity_name',
    itemValue: (item: any) => item,
    dependsOn: 'subject',
    dependsBehavior: 'disabled',
    dependsCondition: { value: null, condition: 'diferent' },
    fetchParams: (form: any) => ({
      'filter[subject_id]': form?.subject.id || null,
      'filter[subject]': form?.subject?.type
    }),
    transform: (value: any) => ({
      opportunity_id: value?.id,
    })
  },
  {
    value: 'quotation_items',
    label: 'Productos',
    isJsonArray: true,
    component: DynamicJsonField,
    col: 12,
    transform: (items: any[]) =>
      items.map(item => ({
        quantity: Number(item.quantity) || 1,
        quoted_item_type: item.productable_type || 'Product',
        quoted_item_id: item.productable_type === 'Product' ? item.item_id : item.productable_id,
        unit_price: item.unit_price,
      })
    ),
    children: [
      {
        value: 'quantity',
        label: 'Cantidad',
        component: VTextField,
        type: 'number',
        required: true,
        col: 2,
      },
      {
        value: 'product_selector',
        label: 'Producto',
        component: ProductSelector,
        col: 10,
        isCustom: true,
        required: true,
        props: (row: Record<string, any>, form: Record<string, any>) => ({
          subject: form?.subject,
        }),

      }
    ],
    summary: {
      show: true,
      fields: ['subtotal'],
      formatter: (items) => {
        const total = items.reduce((acc, item) => {
          const q = parseFloat(item.quantity) || 0;
          const p = parseFloat(item.unit_price) || 0;
          item.subtotal = q * p;
          return acc + item.subtotal;
        }, 0);
        return { total };
      },
    },
  },
  {
    value: 'notes',
    label: 'Notas',
    component: VTextarea,
  },
  {
    value: 'additional_data',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: false,
    component: DynamicJsonField,
    col: 12,
    children: [
      {
        value: 'label',
        label: 'Etiqueta',
        component: VTextField,
        col: 6,
        required: false,
      },
      {
        value: 'file',
        label: 'Archivo',
        component: FileUploader,
        module: 'activities', // importante
        col: 6,
        required: false,
      }
    ],
  },
];

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  if (field.value === 'quotation_items') {
    return {
      ...field,
      parse: (items: any[]) => items.map((item: any) => {
        const quoted = item.quoted_item || {};
        const isProduct = quoted.type === 'products' || quoted.productable_type === 'Product';
        const product_description = quoted?.attributes?.description;
        const product_organization = quoted?.attributes?.organization ?? null;
        return {
          quantity: item.quantity,
          unit_price: item.unit_price,
          productable_type: quoted.attributes.productable_type ?? 'Product',
          productable_id: quoted.id ?? null,
          item_id: isProduct ? quoted.id : null,
          productables: isProduct ? [] : [quoted],
          product_description: product_description ?? null,
          product_organization
        };

      }),
    }
  }
  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: val?.id ?? val
      })
    };
  }
  return field;
})];

export const HEADERS = [
  //{ title: '', value: 'eye_detail', format: 'text', sortable: false },
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Cliente', value: 'subject', format: 'text', sortable: false },
  ...(IS_DOMINIOMEDIOS ? [{ title: 'Campaña', value: 'campaign', format: 'text', sortable: true }] : []),
  { title: 'Subtotal', value: 'subtotal', format: 'currency', sortable: true },
  { title: 'IVA', value: 'iva', format: 'currency', sortable: true },
  { title: 'Total', value: 'total', format: 'currency', sortable: true },
  { title: 'Autorización', value: 'needs_authorization', format: 'text', sortable: true },
  { title: 'Autorizado por', value: 'authorized_by', format: 'text', sortable: true },
  { title: 'Vendedor', value: 'user', format: 'text', sortable: false },
  { title: 'Creación', value: 'created_at', format: 'text', sortable: true },

];

export const EXPAND_HEADERS = [
  { title: 'Producto', value: 'description', format: 'text' },
  { title: 'Organización', value: 'sold_item.attributes.organization.name', format: 'text' },
  { title: 'Cantidad', value: 'quantity', format: 'decimal' },
  { title: 'Precio unitario', value: 'unit_price', format: 'currency' },
  { title: 'Precio unitario sugerido', value: 'suggested_unit_price', format: 'currency' },
  { title: 'Total', value: 'price_with_iva', format: 'currency' },
  { title: 'Autorización', value: 'needs_authorization', format: 'text' },
  { value: 'actions', sortable: false, align: 'end' },
];

export const DEFAULT_HEADER_ORDER = [
  'eye_detail',
  'id',
  'subject',
  IS_DOMINIOMEDIOS ? 'campaign':'',
  'subtotal',
  'iva',
  'total',
  'needs_authorization',
  'authorized_by',
  'user',
  'created_at',
];

export const DETAIL = {
  expand_table: true,
  eye_detail: false,
  key: 'quotation_items',
  component: QuotationDetail,
  custom_expand_detail:false
};

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'needs_authorization', label: 'Necesita Autorización', component: VSelect, options: [
      { label: 'Sí', value: true },
      { label: 'No', value: false },
    ],
    itemText: 'label',
    itemValue: 'value',
  },
  { value: 'subject_id', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    fetchOptions: 'searchClientsAndLeads', 
    searchValue: 'name',
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject: value?.type
    })
  },
  { value: 'user', label: 'Vendedor', component: VAutocomplete, fetchOptions: 'searchUsers', 
    searchValue: 'name',
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
    itemValue: (item: any) => item,
    transform: (values: any) => ({
      user_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'created_between', label: 'Fecha de Creación', component: DatePickerInput, range: true },
  //{ value: 'product_id', label: 'Producto', component: VAutocomplete, fetchOptions: 'searchProducts', searchValue: 'description', itemText: 'attributes.description', itemValue: 'id'},
  { value: 'notes', label: 'Notas', component: VTextField },
];

export const ROW_ACTIONS = [
  { label: 'Editar', onClick: '__edit', permission:'quotations.update' },
  { label: 'Eliminar', onClick: '__delete', permission:'quotations.delete' },
  { label: 'Exportar PDF', onClick: 'exportPdfAndDownload', permission:undefined },
  { label: 'Convertir en Venta', onClick: 'convertQuotation', permission:undefined },
  { label: 'Autorizar', onClick: 'authorizeQuotation', permission:'quotations.authorize', showIf: (item:any) => !!item?.needs_authorization },
];

export const EXPAND_ROW_ACTIONS = [
  { label: 'Editar', onClick: '__edit', permission:'quotation_details.update' },
  { label: 'Eliminar', onClick: '__delete', permission:'quotation_details.delete' },
  //{ label: 'Autorizar', permission:'quotation_details.authorize', onClick: 'authorizeQuotationItem' }
];


export const CONVERT_TO_SALE_FIELDS: FormField[] = [
  /*{
    value: 'additional_data.billing.organization',
    label: 'Empresa que Factura',
    component: VAutocomplete,
    fetchOptions: 'searchOrganizations',
    itemText: (item: any) => item.legal_name,
    itemValue: (item: any) => item,
    required: true,
    col: 6,
    transform: (value: any) => ({
      organization: value?.legal_name,
    }),
  },*/
  {
    value: 'organization',                // 👈 mantiene el valor plano
    label: 'Empresa que Factura',
    component: VAutocomplete,
    options: [
      'GRUPO MASS COMUNICACIONES, SA DE CV',
      'POSTER PUBLICIDAD, SA DE CV',
      'DOMINIO RADIO, SA DE CV',
      'RADIO TELEVISORA DEL VALLE, SA DE CV',
      'MEGA ESTUDIOS, SA DE CV',
      'MEGA TELEVISORA, SA DE CV',
    ],
    // Vuetify acepta función para item-title/item-value; tu getFieldProps ya las pasa
    itemText: (v: string) => v,
    itemValue: (v: string) => v,
    required: true,
    col: 6,
    transform: (value: string) => ({
      organization: value,                // 👈 si antes armabas additional_data.billing.* en el onSubmit, sigue igual
    }),
  },
  {
    value: 'invoice_date',
    label: 'Fecha de facturación',
    component: DatePickerInput,
    required: true,
    col: 6,
  },
  {
    value: 'note',
    label: 'Nota',
    component: VTextarea,
    required: false,
    col: 12,
    transform: (value: string) => ({ note: value ?? '' }),
  },
  {
    value: 'pauta',
    label: 'Pauta',
    component: FileUploader,
    module: 'activities', // importante
    col: 6,
    required: false,
  },
  {
    value: 'orden_de_compra',
    label: 'Orden de Compra',
    component: FileUploader,
    module: 'activities', // importante
    col: 6,
    required: false,
  }
];


export const RENDERERS = {
  user: 'userFullName',
  client: 'clientName',
  needs_authorization: { name: 'chipBoolean', props: { label:'Requiere Autorización', color:'orange' } },
  'additional_data.pauta': { name: 'downloadLink', props: { label: 'Pauta' } },
  'additional_data.orden_de_compra': { name: 'downloadLink', props: { label: 'Orden de Compra' } },
};
